/* Luchos Club multi-step application. Vanilla JS, no dependencies.
 * Test mode builds the exact Klaviyo payloads and shows them; it never sends anything.
 * Live mode posts to Klaviyo's public client API (public company ID only, no private key). */
(() => {
  const KLAVIYO_REVISION = '2024-10-15';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const E164_RE = /^\+[1-9]\d{7,14}$/;

  const track = (name, data = {}) => {
    try { window.Shopify?.analytics?.publish?.(name, data); } catch (e) { /* analytics must never break the form */ }
  };

  const scrollToEl = (el, smooth = true) => {
    const header = document.querySelector('#header-group');
    const offset = (header ? header.getBoundingClientRect().bottom : 0) + 12;
    let sc = el.parentElement;
    while (sc && sc !== document.body && !(sc.scrollHeight > sc.clientHeight && /(auto|scroll)/.test(getComputedStyle(sc).overflowY))) sc = sc.parentElement;
    const behavior = smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto';
    if (!sc || sc === document.body) return window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior });
    sc.scrollTo({ top: el.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop - offset, behavior });
  };

  const readUtm = () => {
    try { return JSON.parse(sessionStorage.getItem('lc_utm') || '{}'); } catch (e) { return {}; }
  };

  const cleanHandle = (v) => v.trim()
    .replace(/^https?:\/\/(www\.)?(instagram\.com|tiktok\.com)\/@?/i, '')
    .replace(/^@+/, '')
    .replace(/[/?#].*$/, '')
    .slice(0, 60);

  const normalisePhone = (code, raw) => {
    let n = raw.replace(/[^\d+]/g, '');
    if (!n) return '';
    if (n.startsWith('+')) return n;
    if (n.startsWith('00')) return '+' + n.slice(2);
    if (!code) return n;
    return code + n.replace(/^0+/, '');
  };

  function init(form) {
    if (form.dataset.lcReady) return;
    form.dataset.lcReady = '1';

    const card = form.closest('.lc-card');
    const section = form.closest('.lc-apply');
    const steps = [...form.querySelectorAll('.lc-step')];
    const indicators = [...section.querySelectorAll('[data-lc-steps] li')];
    const errorBox = form.querySelector('[data-lc-error]');
    const clubOnly = [...form.querySelectorAll('[data-club-only]')];
    const cfg = form.dataset;
    let current = 0;
    let started = false;
    let submitting = false;
    let lastPayload = null;
    let submissionId = null;

    const field = (name) => form.elements[name];
    const val = (name) => (field(name)?.value || '').trim();
    const checked = (name) => [...form.querySelectorAll(`input[name="${name}"]:checked`)].map((i) => i.value);

    const isCustomerRoute = () => {
      const input = form.querySelector('input[name="training_frequency"]:checked');
      if (!input) return false;
      if (input.hasAttribute('data-route-customer')) return true;
      return input.hasAttribute('data-route-less') && cfg.lessRoute !== 'club';
    };

    /* ---------- validation ---------- */
    const setInvalid = (el, bad) => {
      const wrap = el.closest('.lc-field, .lc-group');
      if (!wrap) return;
      wrap.classList.toggle('is-invalid', bad);
      const input = wrap.querySelector('input:not([type=radio]):not([type=checkbox]), select, textarea');
      const err = wrap.querySelector('.lc-error');
      if (input) {
        input.setAttribute('aria-invalid', bad ? 'true' : 'false');
        if (err?.id) input.setAttribute('aria-describedby', err.id);
      }
    };

    // Generic: each step declares what it needs in its markup (required, data-type, data-min, fieldset[data-required]).
    const stepErrors = (scope) => {
      const bad = [];
      scope.querySelectorAll('input:not([type=radio]):not([type=checkbox]):not([name=company]), select, textarea').forEach((el) => {
        if (el.closest('[hidden]')) return;
        const v = el.value.trim();
        let ok = !el.required || v !== '';
        if (ok && v && el.dataset.type === 'email') ok = EMAIL_RE.test(v);
        if (ok && v && el.dataset.type === 'phone') ok = E164_RE.test(normalisePhone(val('phone_code'), v));
        if (ok && el.dataset.min && el.required) ok = v.length >= Number(el.dataset.min);
        if (!ok) bad.push(el);
      });
      scope.querySelectorAll('fieldset[data-required]').forEach((fs) => {
        if (fs.closest('[hidden]') || fs.hidden) return;
        if (!fs.querySelector('input:checked')) bad.push(fs);
      });
      return bad;
    };

    const validateStep = (n) => {
      const scope = steps[n - 1];
      scope.querySelectorAll('.is-invalid').forEach((w) => w.classList.remove('is-invalid'));
      const bad = stepErrors(scope);
      bad.forEach((el) => {
        const wrap = el.closest('.lc-field, .lc-group');
        const err = wrap?.querySelector(':scope > .lc-error, .lc-error');
        const msg = el.dataset.msg || el.closest('[data-msg]')?.dataset.msg;
        if (err && msg) err.textContent = msg;
        setInvalid(el, true);
      });
      if (bad.length) {
        const first = bad[0].matches('fieldset') ? bad[0].querySelector('input') : bad[0];
        first?.focus();
      }
      return bad.length === 0;
    };

    // Clear an error as soon as the visitor fixes it.
    form.addEventListener('input', (e) => {
      const wrap = e.target.closest('.is-invalid');
      if (wrap) { wrap.classList.remove('is-invalid'); e.target.setAttribute('aria-invalid', 'false'); }
      if (!started) { started = true; track('lc_application_start', { step: 1 }); }
    });
    form.addEventListener('change', (e) => {
      if (e.target.matches('[data-handle]')) e.target.value = cleanHandle(e.target.value);
      if (e.target.name === 'training_frequency') updateBranch();
      const wrap = e.target.closest('.is-invalid');
      if (wrap) wrap.classList.remove('is-invalid');
    });

    const updateBranch = () => {
      const customer = isCustomerRoute();
      clubOnly.forEach((el) => { el.hidden = customer; });
      const btn = form.querySelector('[data-branch] button[type=submit]');
      if (btn) {
        if (!btn.dataset.next) btn.dataset.next = btn.innerHTML;
        btn.innerHTML = customer ? (cfg.customerLabel || 'Continue') : btn.dataset.next;
      }
    };

    /* ---------- navigation ---------- */
    const showStep = (i, focus = true) => {
      current = i;
      steps.forEach((s, idx) => { s.hidden = idx !== i; });
      indicators.forEach((li, idx) => {
        li.classList.toggle('is-active', idx === i);
        li.classList.toggle('is-done', idx < i);
        if (idx === i) li.setAttribute('aria-current', 'step'); else li.removeAttribute('aria-current');
      });
      hideError();
      if (focus) {
        steps[i].querySelector('.lc-step__title')?.focus({ preventScroll: true });
        const top = card.getBoundingClientRect().top;
        if (top < 80 || top > window.innerHeight * 0.6) scrollToEl(card);
      }
    };

    form.querySelectorAll('[data-lc-back]').forEach((b) => b.addEventListener('click', () => showStep(Math.max(0, current - 1))));

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (submitting) return;
      const n = current + 1;
      if (!validateStep(n)) return;
      track('lc_step_complete', { step: n, step_name: indicators[current]?.textContent.trim() || String(n) });

      if (steps[current].hasAttribute('data-branch') && isCustomerRoute()) return submit('customer');
      if (n < steps.length) return showStep(current + 1);
      return submit('club');
    });

    /* ---------- payload ---------- */
    const buildPayload = (route) => {
      const utm = readUtm();
      const phone = normalisePhone(val('phone_code'), val('phone'));
      const consent = !!field('email_consent')?.checked;
      // Property names match the client's Klaviyo form (R4Mvv6) so existing segments and flows keep working.
      const props = {
        'Luchos Club Application': true,
        'Luchos Club Source': cfg.source,
        'Luchos Club Application Route': route === 'club' ? 'Club Review' : 'Customer',
        'Luchos Club Application Status': route === 'club' ? 'Pending Review' : 'Not Training - Customer Flow',
        'Luchos Club Application Date': new Date().toISOString(),
        'Luchos Club Location': val('location'),
        'Luchos Club Instagram Handle': val('instagram') ? '@' + cleanHandle(val('instagram')) : '',
        'Luchos Club TikTok Handle': val('tiktok') ? '@' + cleanHandle(val('tiktok')) : '',
        'Luchos Club Training Frequency': checked('training_frequency')[0] || '',
        'Luchos Club Email Marketing Consent': consent,
      };
      if (route === 'club') {
        props['Luchos Club Training Types'] = checked('training_type');
        props['Luchos Club Training Goal'] = val('training_notes');
        props['Luchos Club Content Frequency'] = checked('content_frequency')[0] || '';
        props['Luchos Club Content Type'] = checked('content_type');
        props['Luchos Club Application Reason'] = val('why_join');
      }
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'fbclid', 'gclid', 'ttclid', 'landing_page'].forEach((k) => {
        if (utm[k]) props[k.startsWith('utm_') ? 'UTM ' + k.slice(4).replace(/^./, (c) => c.toUpperCase()) : k === 'landing_page' ? 'Landing Page' : k] = utm[k];
      });
      Object.keys(props).forEach((k) => (props[k] === '' || (Array.isArray(props[k]) && !props[k].length)) && delete props[k]);

      const profile = {
        email: val('email').toLowerCase(),
        first_name: val('first_name'),
        last_name: val('last_name'),
        properties: props,
      };
      if (E164_RE.test(phone)) profile.phone_number = phone;
      if (val('location')) profile.location = { country: val('location') };

      const event = {
        data: {
          type: 'event',
          attributes: {
            properties: props,
            metric: { data: { type: 'metric', attributes: { name: cfg.metric } } },
            profile: { data: { type: 'profile', attributes: profile } },
            // Same id on every retry, so Klaviyo keeps one application if an earlier attempt did arrive.
            unique_id: submissionId || (submissionId = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random().toString(36).slice(2)),
          },
        },
      };
      // Club applicants go to the applicants list. Non-trainers must NOT enter that review list,
      // so they only get a subscription when a separate customer list is configured.
      const listId = route === 'club' ? cfg.list : cfg.customerList;
      const subscription = consent && listId ? buildSubscription(profile.email, listId) : null;
      return { route, event, subscription };
    };

    const buildSubscription = (email, listId) => ({
      data: {
        type: 'subscription',
        attributes: {
          custom_source: cfg.source,
          // Subscribing through this endpoint records email marketing consent for the list.
          profile: { data: { type: 'profile', attributes: { email } } },
        },
        relationships: { list: { data: { type: 'list', id: listId } } },
      },
    });

    const post = async (path, body) => {
      // Give up after 20s so a stalled connection shows a retry message instead of "Sending…" forever.
      const ctrl = 'AbortController' in window ? new AbortController() : null;
      const timer = ctrl && setTimeout(() => ctrl.abort(), 20000);
      let res;
      try {
        res = await fetch(`https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(cfg.company)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
          body: JSON.stringify(body),
          signal: ctrl?.signal,
        });
      } finally {
        if (timer) clearTimeout(timer);
      }
      if (res.status !== 202 && !res.ok) {
        let detail = '';
        try { detail = (await res.json()).errors?.[0]?.detail || ''; } catch (e) {}
        throw new Error(detail || `Klaviyo returned ${res.status}`);
      }
    };

    /* ---------- submit ---------- */
    const setBusy = (busy) => {
      submitting = busy;
      steps[current].querySelectorAll('button').forEach((b) => { b.disabled = busy; });
      const btn = steps[current].querySelector('button[type=submit]');
      if (btn) {
        if (busy) { btn.dataset.label = btn.innerHTML; btn.textContent = 'Sending…'; btn.setAttribute('aria-busy', 'true'); }
        else if (btn.dataset.label) { btn.innerHTML = btn.dataset.label; btn.removeAttribute('aria-busy'); }
      }
    };
    const showError = (msg) => { errorBox.textContent = msg; errorBox.classList.add('is-visible'); };
    const hideError = () => { errorBox.textContent = ''; errorBox.classList.remove('is-visible'); };

    const showDebug = (payload) => {
      const dbg = card.querySelector('[data-lc-debug]');
      if (!dbg) return;
      dbg.hidden = false;
      dbg.querySelector('pre').textContent = JSON.stringify(payload, null, 2);
    };

    async function submit(route) {
      if (val('company')) return; // honeypot: bots fill hidden fields
      setBusy(true);
      hideError();
      const payload = buildPayload(route);
      lastPayload = payload;
      try {
        if (cfg.mode === 'live') {
          try {
            await post('events', payload.event);
          } catch (err) {
            // Klaviyo rejects the whole request for a phone number it can't validate.
            // Keep the application: resend without phone_number and store the typed number as a property.
            const attrs = payload.event.data.attributes.profile.data.attributes;
            if (!attrs.phone_number || !/phone/i.test(err.message)) throw err;
            attrs.properties['Luchos Club Mobile (unverified)'] = attrs.phone_number;
            delete attrs.phone_number;
            await post('events', payload.event);
          }
          // The application is saved at this point. A failed list signup must not show an error
          // (the visitor would resubmit and create a duplicate application).
          if (payload.subscription) {
            try { await post('subscriptions', payload.subscription); }
            catch (subErr) { console.error('[Luchos Club] list signup failed (application was saved)', subErr); }
          }
        } else {
          console.info('[Luchos Club] TEST MODE, nothing sent. Payload:', payload);
          showDebug(payload);
        }
      } catch (err) {
        setBusy(false);
        showError("Sorry, we couldn't send your application. Please check your connection and try again.");
        console.error('[Luchos Club] submission failed', err);
        return;
      }
      track('lc_application_submit', { route });
      showResult(route);
    }

    const showResult = (route) => {
      form.hidden = true;
      indicators.forEach((li) => { li.classList.remove('is-active'); li.classList.add('is-done'); });
      const box = card.querySelector(`[data-lc-result="${route}"]`);
      box.hidden = false;
      if (route === 'customer') {
        // Only promise the emailed offer when there is a customer list to subscribe them to.
        const consent = !!field('email_consent')?.checked;
        const canEmail = !!cfg.customerList;
        box.querySelector('[data-lc-consented]').hidden = !(consent && canEmail);
        box.querySelector('[data-lc-needs-consent]').hidden = consent || !canEmail;
      }
      box.querySelector('[tabindex="-1"]')?.focus({ preventScroll: true });
      scrollToEl(card);
    };

    // Customer route without consent: let them opt in to receive the offer.
    card.querySelector('[data-lc-optin]')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      btn.disabled = true;
      const sub = cfg.customerList ? buildSubscription(val('email').toLowerCase(), cfg.customerList) : null;
      try {
        if (cfg.mode === 'live') {
          if (sub) await post('subscriptions', sub);
          // Update only the profile property. Re-sending the application event would count the
          // application twice and could trigger the flow twice.
          await post('profiles', {
            data: { type: 'profile', attributes: { email: val('email').toLowerCase(), properties: { 'Luchos Club Email Marketing Consent': true } } },
          });
        } else {
          console.info('[Luchos Club] TEST MODE opt-in, nothing sent:', sub || '(no customer list ID configured)');
          showDebug({ optin: true, subscription: sub || '(no customer list ID configured)' });
        }
        const box = btn.closest('[data-lc-result]');
        box.querySelector('[data-lc-needs-consent]').hidden = true;
        box.querySelector('[data-lc-consented]').hidden = false;
        track('lc_customer_optin', {});
      } catch (err) {
        btn.disabled = false;
        console.error('[Luchos Club] opt-in failed', err);
      }
    });

    updateBranch();
  }

  const boot = () => document.querySelectorAll('[data-lc-form]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
